import React, { useEffect, useState } from 'react'
import { fileServices } from './fileServices';
import { Box, Grid, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogContent, Button, DialogTitle, DialogActions } from '@mui/material';
import { FolderItem, FileItem } from './FileItems';
import { InsertDriveFile, Download, Close, Delete, KeyboardArrowLeft, KeyboardArrowRight, ContentCopy } from '@mui/icons-material';

const getFileType = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
  const pdfExts = ['pdf'];
  const videoExts = ['mp4', 'mov', 'avi', 'mkv', 'webm'];

  if (imageExts.includes(ext)) return 'image';
  if (pdfExts.includes(ext)) return 'pdf';
  if (videoExts.includes(ext)) return 'video';
  return 'other';
};

const ListPreviewDialog = ({ file, allFiles, open, onClose }) => {
  const fileType = getFileType(file?.fileName);
  const imageFiles = allFiles?.filter(f => getFileType(f.fileName) === 'image') || [];
  const isMultipleImages = imageFiles.length > 1;
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(imageFiles.findIndex(f => f.fileName === file.fileName));
  }, [file, imageFiles]);

  const handlePrevious = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : imageFiles.length - 1);
  };

  const handleNext = () => {
    setCurrentIndex(prev => prev < imageFiles.length - 1 ? prev + 1 : 0);
  };

  const renderPreview = () => {
    if (fileType === 'image' && isMultipleImages) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
          <Box sx={{ position: 'relative', width: '100%', maxHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img 
              src={imageFiles[currentIndex]?.url} 
              alt={imageFiles[currentIndex]?.fileName} 
              style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }} 
            />
            {imageFiles.length > 1 && (
              <>
                <IconButton onClick={handlePrevious} sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: '#1976d2', color: '#fff' }}>
                  <KeyboardArrowLeft />
                </IconButton>
                <IconButton onClick={handleNext} sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: '#1976d2', color: '#fff' }}>
                  <KeyboardArrowRight />
                </IconButton>
              </>
            )}
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>{currentIndex + 1} of {imageFiles.length}</Typography>
        </Box>
      );
    }

    switch (fileType) {
      case 'image':
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', maxHeight: '70vh' }}>
            <img 
              src={file.url} 
              alt={file.fileName} 
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} 
            />
          </Box>
        );
      case 'pdf':
        return (
          <iframe
            src={file.url}
            title={file.fileName}
            style={{ width: '100%', height: '70vh', border: 'none' }}
          />
        );
      case 'video':
        return (
          <video
            controls
            autoPlay
            style={{ width: '100%', maxHeight: '70vh' }}
          >
            <source src={file.url} />
            Your browser does not support the video tag.
          </video>
        );
      default:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <InsertDriveFile sx={{ fontSize: 80, color: '#757575' }} />
            <Typography variant="h6" sx={{ mt: 2 }}>Preview not available</Typography>
            <Button 
              variant="contained" 
              startIcon={<Download />}
              href={file.url}
              target="_blank"
              sx={{ mt: 2 }}
            >
              Download File
            </Button>
          </Box>
        );
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg"
      fullWidth
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{ 
            position: 'absolute', 
            right: 8, 
            top: 8, 
            zIndex: 1,
            bgcolor: 'rgba(255,255,255,0.8)',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
          }}
        >
          <Close />
        </IconButton>
        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
          <Typography variant="subtitle1" fontWeight={600}>{file?.fileName}</Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {renderPreview()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const FileListItem = ({ file, onPreview, onDelete }) => {
  const fileType = getFileType(file.fileName);
  
  const handleClick = () => {
    onPreview(file);
  };

  return (
    <TableRow hover sx={{ cursor: 'pointer' }} onClick={handleClick}>
      <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1,  }}>
        {/* {fileType === 'image' ? (
          <Box sx={{ width: 40, height: 40, borderRadius: 1, overflow: 'hidden' }}>
            <img src={file.url} alt={file.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
        ) : fileType === 'pdf' ? (
          <InsertDriveFile sx={{ color: '#f44336', fontSize: 28 }} />
        ) : fileType === 'video' ? (
          <InsertDriveFile sx={{ color: '#2196f3', fontSize: 28 }} />
        ) : (
          <InsertDriveFile sx={{ color: '#757575', fontSize: 28 }} />
        )} */}
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{file.fileName}</Typography>
      </TableCell>
      <TableCell sx={{}}>
        <Typography sx={{ fontWeight: 500, color: '#757575', fontSize:'0.5rem' }}>{fileType.toUpperCase()}</Typography>
      </TableCell>
      {/* <TableCell sx={{}}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{file.filePath}</Typography>
      </TableCell> */}
      <TableCell sx={{}}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* copy url */}
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(file.url); }}>
            <ContentCopy fontSize="small" />
          </IconButton>

          {/* <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete(file); }}>
            <Delete fontSize="small" />
          </IconButton> */}
        </Box>
      </TableCell>
    </TableRow>
  );
};

const FolderContent = ({folder, setCurrentPath, setPathHistory, pathHistory, viewMode = 'grid'}) => {
    const [folderContent, setFolderContent] = useState({folders: [], files: []});
    const [loading, setLoading] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [deleteFile, setDeleteFile] = useState(null);

    const fetchFolderContent = async () => {
        setLoading(true);
        try {
            const response = await fileServices.fetchFolderContent(folder);
            if(response.success == '1'){
                setFolderContent(response.data);
            }
        } finally {
            setLoading(false);
        }
    }
    useEffect(()=>{
      // if folder is null, undefined, or empty, we are at root level — skip fetch
        if(folder && folder.length > 0){
            fetchFolderContent();
        }
    },[folder])

    const handleSubFolderClick = (subFolder) => {
        const newPath = `${folder}/${subFolder}`;
        setPathHistory([...pathHistory, newPath]);
        setCurrentPath(newPath);
    }

    const handlePreviewFile = (file) => {
        setPreviewFile(file);
    };

    const handleClosePreview = () => {
        setPreviewFile(null);
    };

    const handleDeleteClick = (file) => {
        setDeleteFile(file);
    };

    const handleConfirmDelete = async () => {
        if (deleteFile) {
            try {
                const response = await fileServices.deleteFile(deleteFile.filePath);
                if (response.success == '1') {
                    fetchFolderContent();
                }
            } catch (error) {
                console.error('Delete error:', error);
            }
        }
        setDeleteFile(null);
    };

    const handleCloseDelete = () => {
        setDeleteFile(null);
    };

  return (
    <Box sx={{width:'100%',}}>
        {loading ? (
            <Box sx={{display:'flex',justifyContent:'center',alignItems:'center',height:'200px'}}>
                <CircularProgress />
            </Box>
        ) : viewMode === 'list' ? (
            <TableContainer component={Paper} sx={{ boxShadow: 'none',maxHeight:'70vh',overflowY:'auto' }}>
                <Table stickyHeader>
                    <TableHead >
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600, }}>Type</TableCell>
                            {/* <TableCell sx={{ fontWeight: 600, }}>Path</TableCell> */}
                            <TableCell sx={{ fontWeight: 600, }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{maxHeight:'60vh',overflowY:'auto'}}>
                        {folderContent.folders && folderContent.folders.length > 0 && folderContent.folders.map((subFolder, index) => (
                            <TableRow hover key={`list-folder-${index}`} sx={{ cursor: 'pointer' }} onClick={() => handleSubFolderClick(subFolder)}>
                                <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 , }}>
                                    <img src={require('../../../assets/images/folder.png')} alt={subFolder} style={{ width: 32, height: 32 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{subFolder}</Typography>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500, color: '#757575', fontSize:'0.5rem' }}>FOLDER</TableCell>
                                {/* <TableCell sx={{}}>{folder}/{subFolder}</TableCell> */}
                                <TableCell sx={{}}></TableCell>
                            </TableRow>
                        ))}
                        {folderContent.files && folderContent.files.length > 0 && folderContent.files.map((file, index) => (
                            <FileListItem key={`list-file-${index}`} file={file} onPreview={handlePreviewFile} onDelete={handleDeleteClick} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        ) : (
      <Grid container>
        {folderContent.files && folderContent.folders && (folderContent.files.length === 0 && folderContent.folders.length === 0) ?
          <Grid item xs={12}>
            <Typography variant='h6' align='center'>No content found in this folder</Typography>
          </Grid>
        :
        <>
          {folderContent.folders && folderContent.folders.length > 0 && folderContent.folders.map((subFolder,index)=>(
            <Grid item xs={3} sm={2} md={1} key={`folder-${index}`}>
              <FolderItem folder={subFolder} onDoubleClick={() => handleSubFolderClick(subFolder)} />
            </Grid>
            ))}
            {folderContent.files && folderContent.files.length > 0 && folderContent.files.map((file,index)=>(
                <Grid item xs={3} sm={2} md={1} key={`file-${index}`}>
                  <FileItem file={file} allFiles={folderContent.files} />
                </Grid>
            ))}
        </>
        }
      </Grid>
        )}
        {previewFile && (
            <ListPreviewDialog file={previewFile} allFiles={folderContent.files} open={!!previewFile} onClose={handleClosePreview} />
        )}
        <Dialog open={!!deleteFile} onClose={handleCloseDelete}>
            <DialogTitle>Delete File</DialogTitle>
            <DialogContent>
                <Typography>Are you sure you want to delete "{deleteFile?.fileName}"?</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDelete}>Cancel</Button>
                <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
            </DialogActions>
        </Dialog>
    </Box>
  )
}

export default FolderContent
